import * as THREE from 'three';
import {
  CHASE_CAMERA_OFFSET,
  CHASE_CAMERA_LOOK_AHEAD,
  CHASE_REFERENCE_SHIP_SCALE as SHARED_CHASE_REFERENCE_SHIP_SCALE,
} from '@viral-space/shared';

export type CameraMode = 'chase' | 'cockpit' | 'free';

interface CameraTarget {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
}

// Camera sits a bit further back and higher than the ship, while the lookAt
// aims near the horizon — this keeps the ship in the lower portion of the
// screen and ensures the center crosshair always appears above (not behind)
// the hull when thrusting forward.
//
// These constants are tuned for the Fighter / Interceptor (visual scale
// 3.2). Larger hulls (Cruiser scale 13, Mothership scale 48) need the
// offset scaled up proportionally — otherwise the camera ends up
// *inside* the ship, which is exactly what was happening to every
// player Cruiser+ before {@link CameraSystem.setShipScale} was wired in.
// See the scale logic inside `updateChase` for how the per-class
// multiplier is derived from the active ship's `SHIP_CLASS_VISUAL_SCALE`.
//
// Tuning history: the original offset of (0, 3.5, 14) had even an
// Interceptor occupying ~20% of screen height, which left the player
// peering around their own hull for incoming threats. Pulling the
// camera back to 19 and up to 4.6 reduces the apparent ship size by
// ~28% without changing the framing rules — the ship still sits in the
// lower third with the crosshair clear of the cockpit. Look-ahead is
// pulled forward in step so the horizon target tracks the same screen
// position; otherwise the crosshair drifts upward as the camera moves
// back, which feels like the ship is "tilting up" at rest.
//
// The numeric values live in `@viral-space/shared` so the server can
// derive the matching projectile firing pitch bias
// (`PLAYER_FIRING_PITCH_BIAS_RAD`) from the same source — moving these
// vectors here would silently desync the crosshair and bullet impact
// point. Tweak them in shared/constants/game.ts.
const CHASE_OFFSET = new THREE.Vector3(
  CHASE_CAMERA_OFFSET.x,
  CHASE_CAMERA_OFFSET.y,
  CHASE_CAMERA_OFFSET.z,
);
const CHASE_LOOK_AHEAD = new THREE.Vector3(
  CHASE_CAMERA_LOOK_AHEAD.x,
  CHASE_CAMERA_LOOK_AHEAD.y,
  CHASE_CAMERA_LOOK_AHEAD.z,
);
const COCKPIT_OFFSET = new THREE.Vector3(0, 0.8, 0.5);

/**
 * Visual scale the chase-camera offset is calibrated for. Anything
 * larger than this scales the camera back proportionally so a Cruiser
 * (13) or Mothership (48) still has its bounding sphere comfortably in
 * front of the camera. Smaller hulls (Scout 2.2) keep the baseline
 * offset — pulling the camera in tighter on small ships isn't worth
 * the framing churn.
 *
 * Kept as a local re-export of the shared constant so tests + tooling
 * that import `CHASE_REFERENCE_SHIP_SCALE` from this module keep
 * working unchanged.
 */
const CHASE_REFERENCE_SHIP_SCALE = SHARED_CHASE_REFERENCE_SHIP_SCALE;

// Camera position tracks the ship rigidly, and the lookAt now tracks it
// almost as tightly. We keep a tiny amount of smoothing (lerp speed
// ≈ 100 → per-frame catch-up ≈ 81% at 60fps) so large rotation jumps
// from server reconciliation / respawn / warp don't produce a single-
// frame snap, but the steady-state trail under a sustained touch-stick
// push is now ~1.6° (≈ 18ms of visual lag) — well below the perceptual
// latency floor.
//
// History: the old value of 14 produced an ~8° trail that read as "the
// camera is delayed" on mobile, because a held stick yields a constant
// rotation rate (mouse flicks are short bursts, so the trail is
// intermittent on desktop and never perceived). Bumping through 30 →
// 100 shrinks the trail by ~5× without losing the smoothing that hides
// micro-jitter on non-input-driven rotation changes.
const CHASE_LOOKAT_LERP_SPEED = 100.0;
const COCKPIT_LERP_SPEED = 100.0;

export class CameraSystem {
  private camera: THREE.PerspectiveCamera;
  private mode: CameraMode = 'chase';
  private targetPosition = new THREE.Vector3();
  private targetLookAt = new THREE.Vector3();
  private currentLookAt = new THREE.Vector3();

  private freeMoveSpeed = 20;
  private freeRotateSpeed = 0.002;
  private freeEuler = new THREE.Euler(0, 0, 0, 'YXZ');
  private freeVelocity = new THREE.Vector3();

  private _tmpVec = new THREE.Vector3();
  private _tmpQuat = new THREE.Quaternion();

  private _shakeTimer = 0;
  private _shakeIntensity = 0;

  /**
   * Visual scale of the player's current ship. Mutated by
   * {@link setShipScale} when the hangar swaps active hulls; consumed
   * by {@link updateChase} to push the camera back proportionally so
   * the camera never ends up inside a Cruiser or Mothership.
   */
  private _shipScale = CHASE_REFERENCE_SHIP_SCALE;
  private _scaledChaseOffset = new THREE.Vector3();
  private _scaledChaseLookAhead = new THREE.Vector3();

  /**
   * When `true`, the chase camera keeps its `up` vector locked to
   * world-up rather than ship-local-up. Used on planetary surfaces so
   * banking and rolling don't tilt the horizon (which is nauseating
   * over terrain). Defaults to `false` so space flight retains its
   * full 6DoF feel where rolling the ship rolls the camera.
   */
  private _worldUpOnRoll = false;
  private static readonly WORLD_UP = new THREE.Vector3(0, 1, 0);

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(65, aspect, 0.1, 20000);
    this.camera.position.set(0, 5, 20);
    this.currentLookAt.set(0, 0, 0);
    this._scaledChaseOffset.copy(CHASE_OFFSET);
    this._scaledChaseLookAhead.copy(CHASE_LOOK_AHEAD);
  }

  /**
   * Update the camera's framing for the given ship visual scale (one
   * of {@link SHIP_CLASS_VISUAL_SCALE}'s values). The chase offset is
   * stretched along Z + Y proportionally so a Cruiser (scale 13) sits
   * the same distance behind the camera, in screen space, as a Fighter
   * (scale 3.2) does at the baseline. Idempotent + cheap — safe to
   * call every tick from the main loop.
   */
  /**
   * Toggle world-up camera mode. Call with `true` on `Land`, `false`
   * on `Liftoff`. Idempotent — safe to call every tick.
   */
  setWorldUpOnRoll(enabled: boolean): void {
    this._worldUpOnRoll = enabled;
  }

  setShipScale(scale: number): void {
    const safe = Math.max(0.1, scale);
    if (safe === this._shipScale) return;
    this._shipScale = safe;
    const mul = Math.max(1, safe / CHASE_REFERENCE_SHIP_SCALE);
    this._scaledChaseOffset.set(
      CHASE_OFFSET.x,
      CHASE_OFFSET.y * mul,
      CHASE_OFFSET.z * mul,
    );
    this._scaledChaseLookAhead.set(
      CHASE_LOOK_AHEAD.x,
      CHASE_LOOK_AHEAD.y * mul,
      CHASE_LOOK_AHEAD.z * mul,
    );
  }

  shake(duration: number, intensity: number): void {
    this._shakeTimer = duration;
    this._shakeIntensity = intensity;
  }

  update(target: CameraTarget | null, dt: number): void {
    switch (this.mode) {
      case 'chase':
        this.updateChase(target, dt);
        break;
      case 'cockpit':
        this.updateCockpit(target, dt);
        break;
      case 'free':
        this.updateFree(dt);
        break;
    }

    if (this._shakeTimer > 0) {
      this._shakeTimer -= dt;
      const t = Math.max(0, this._shakeTimer);
      const decay = t / 0.15;
      const amp = this._shakeIntensity * decay;
      this.camera.position.x += (Math.random() - 0.5) * amp;
      this.camera.position.y += (Math.random() - 0.5) * amp;
      this.camera.position.z += (Math.random() - 0.5) * amp * 0.3;
    }
  }

  setMode(mode: CameraMode): void {
    this.mode = mode;
    if (mode === 'free') {
      this.freeEuler.setFromQuaternion(this.camera.quaternion);
      this.freeVelocity.set(0, 0, 0);
    }
  }

  getMode(): CameraMode {
    return this.mode;
  }

  getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  /** Call on window resize */
  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /** For free camera WASD input (values -1..1) */
  setFreeInput(forward: number, right: number, up: number): void {
    this.freeVelocity.set(right, up, -forward);
  }

  /** For free camera mouse look (pixel deltas) */
  applyFreeLook(dx: number, dy: number): void {
    this.freeEuler.y -= dx * this.freeRotateSpeed;
    this.freeEuler.x -= dy * this.freeRotateSpeed;
    this.freeEuler.x = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, this.freeEuler.x));
  }

  private updateChase(target: CameraTarget | null, dt: number): void {
    if (!target) return;

    const offset = this._tmpVec
      .copy(this._scaledChaseOffset)
      .applyQuaternion(target.quaternion);
    this.targetPosition.copy(target.position).add(offset);

    const lookAhead = this._tmpVec
      .copy(this._scaledChaseLookAhead)
      .applyQuaternion(target.quaternion);
    this.targetLookAt.copy(target.position).add(lookAhead);

    // Snap the camera position rigidly to the ship so accelerating/strafing
    // never moves the ship on screen. Only the lookAt is lightly damped so
    // rotating with the mouse produces a subtle sway.
    this.camera.position.copy(this.targetPosition);
    const t = 1 - Math.exp(-CHASE_LOOKAT_LERP_SPEED * dt);
    this.currentLookAt.lerp(this.targetLookAt, t);
    if (this._worldUpOnRoll) {
      // World-up mode pins the camera's up vector to +Y so banking
      // the ship doesn't roll the horizon — exactly what hover-tank
      // games like Battlezone do on the ground.
      this.camera.up.copy(CameraSystem.WORLD_UP);
    } else {
      // Restore the default Three.js up vector for chase + cockpit
      // space flight; mutating `up` in surface mode and never
      // clearing it would silently bias the lookAt forever.
      this.camera.up.set(0, 1, 0);
    }
    this.camera.lookAt(this.currentLookAt);
  }

  private updateCockpit(target: CameraTarget | null, dt: number): void {
    if (!target) return;

    const offset = this._tmpVec.copy(COCKPIT_OFFSET).applyQuaternion(target.quaternion);
    this.targetPosition.copy(target.position).add(offset);

    // Rigid position, softly-damped orientation.
    this.camera.position.copy(this.targetPosition);
    const t = 1 - Math.exp(-COCKPIT_LERP_SPEED * dt);
    this._tmpQuat.copy(target.quaternion);
    this.camera.quaternion.slerp(this._tmpQuat, t);
  }

  private updateFree(dt: number): void {
    this.camera.quaternion.setFromEuler(this.freeEuler);

    const move = this._tmpVec
      .copy(this.freeVelocity)
      .multiplyScalar(this.freeMoveSpeed * dt);
    move.applyQuaternion(this.camera.quaternion);
    this.camera.position.add(move);
  }
}
